/**
 * Utility functions for consistent date formatting across the application
 * Handles month-year dates without timezone conversion issues
 */

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Formats a YYYY-MM date string to display format without timezone issues
 * @param dateString - Date in YYYY-MM format (e.g., "2026-02")
 * @param format - Display format: 'short' (Jan 2026) or 'long' (January 2026)
 * @returns Formatted date string or '--' if invalid/empty
 */
export function formatMonthYear(
  dateString: string | null | undefined,
  format: 'short' | 'long' = 'short'
): string {
  if (!dateString || !/^\d{4}-\d{2}$/.test(dateString)) {
    return '--';
  }

  const [year, month] = dateString.split('-');
  const monthIndex = parseInt(month, 10) - 1;

  if (monthIndex < 0 || monthIndex > 11) {
    return '--';
  }

  const monthNames = format === 'short' ? MONTH_NAMES : FULL_MONTH_NAMES;
  return `${monthNames[monthIndex]} ${year}`;
}

/**
 * Parses a YYYY-MM date string and returns month and year components
 * @param dateString - Date in YYYY-MM format
 * @returns Object with month name, month number, and year
 */
export function parseMonthYear(dateString: string): {
  monthName: string;
  monthNumber: number;
  year: number;
} | null {
  if (!dateString || !/^\d{4}-\d{2}$/.test(dateString)) {
    return null;
  }

  const [year, month] = dateString.split('-');
  const monthIndex = parseInt(month, 10) - 1;

  if (monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  return {
    monthName: FULL_MONTH_NAMES[monthIndex],
    monthNumber: parseInt(month, 10),
    year: parseInt(year, 10)
  };
}

/**
 * Compares two YYYY-MM date strings
 * @param date1 - First date in YYYY-MM format
 * @param date2 - Second date in YYYY-MM format
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareMonthYear(date1: string, date2: string): number {
  if (!date1 || !date2) return 0;
  
  const [year1, month1] = date1.split('-').map(Number);
  const [year2, month2] = date2.split('-').map(Number);
  
  if (year1 !== year2) {
    return year1 < year2 ? -1 : 1;
  }
  
  if (month1 !== month2) {
    return month1 < month2 ? -1 : 1;
  }
  
  return 0;
}

/**
 * Validates if a string is in YYYY-MM format
 * @param dateString - String to validate
 * @returns true if valid YYYY-MM format
 */
export function isValidMonthYear(dateString: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(dateString)) {
    return false;
  }
  
  const [, month] = dateString.split('-');
  const monthNum = parseInt(month, 10);
  
  return monthNum >= 1 && monthNum <= 12;
}

/**
 * Gets the current month-year in YYYY-MM format
 * @returns Current month-year string
 */
export function getCurrentMonthYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Adds or subtracts months from a YYYY-MM date
 * @param dateString - Date in YYYY-MM format
 * @param monthsToAdd - Number of months to add (negative to subtract)
 * @returns New date in YYYY-MM format
 */
export function addMonths(dateString: string, monthsToAdd: number): string {
  if (!isValidMonthYear(dateString)) {
    return dateString;
  }
  
  const [year, month] = dateString.split('-').map(Number);
  let newMonth = month + monthsToAdd;
  let newYear = year;
  
  while (newMonth > 12) {
    newMonth -= 12;
    newYear++;
  }
  
  while (newMonth < 1) {
    newMonth += 12;
    newYear--;
  }
  
  return `${newYear}-${newMonth.toString().padStart(2, '0')}`;
}